import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  ComponentType,
} from 'react';

import { LDContext } from 'launchdarkly-js-client-sdk';

import {
  type Env,
  type Partition,
  ENVS,
  PARTITIONS,
  getEnv,
  getPartition,
  getRegion,
  isGovPartition,
} from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext } from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { useOxygenId } from './hooks/useOxygenId';
import createLaunchDarklyContext, {
  DEFAULT_ANONYMOUS_USER_ID,
  LaunchDarklyContextValue,
  LaunchDarklyProviderProps,
} from './LaunchDarklyContext';
import type { EntitlementsUser } from './projectEntitlementTypes';
import { useAlloyLaunchDarklyStore } from './stores/AlloyLaunchDarklyStore';
import {
  useFeatureFlagCore,
  useFeatureFlagOnceCore,
  useAllFeatureFlagsCore,
} from './useFeatureFlagCore';

const LD_API_KEYS: Partial<Record<Partition, Partial<Record<Env, string>>>> = {
  [PARTITIONS.COMMERCIAL]: {
    [ENVS.QA]: '5e4c38359d07d807cfb6fc26',
    [ENVS.STAGING]: '5e4c3835aadaaf0815842d0e',
    [ENVS.PRODUCTION]: '5e4c3834d497f707dd287f20',
  },
  [PARTITIONS.GOVCLOUD]: {
    [ENVS.STAGING]: '6a534e29cd87b60ba57fb026',
    [ENVS.PRODUCTION]: '6a534e2c3922060bcc1d14c7',
  },
};

export const AlloyLaunchDarklyContext = getOrCreateGlobalContext<
  LaunchDarklyContextValue | undefined
>(CONTEXT_NAMES.AlloyLaunchDarklyContext);

const { SimpleLaunchDarklyProvider } = createLaunchDarklyContext(
  AlloyLaunchDarklyContext,
);

type AlloyLaunchDarklyContextValue = {
  projectId?: string | undefined;
  accountId?: string | undefined;
  user?: EntitlementsUser | undefined;
  setUser: (user: EntitlementsUser | undefined) => void;
  setProjectId: (projectId: string | undefined) => void;
  setAccountId: (accountId: string | undefined) => void;
};

const AlloyLaunchDarklyUserContext =
  createContext<AlloyLaunchDarklyContextValue>({
    setUser: noop,
    setProjectId: noop,
    setAccountId: noop,
    user: undefined,
  });

const useAlloyLaunchDarklyUser = (
  user: EntitlementsUser | undefined,
  region: string,
  accountId: string | undefined,
  projectId: string | undefined,
): LDContext => {
  const { analyticsId, email } = user || {};

  const { oxygenId } = useOxygenId();

  // Afg (GovCloud) compliance forbids sending PII to LaunchDarkly. Omit
  // `email` and `oxygenId` entirely for Afg; COMMERCIAL behavior unchanged.
  const isAfg = isGovPartition();

  return useMemo(() => {
    const custom = { region };

    if (accountId) {
      Object.assign(custom, { accountId });
    }
    if (projectId) {
      Object.assign(custom, { projectId });
    }

    if (oxygenId && !isAfg) {
      Object.assign(custom, { oxygenId });
    }

    return {
      kind: 'user',
      key: analyticsId || DEFAULT_ANONYMOUS_USER_ID,
      anonymous: true,
      // Omit the key entirely (not `undefined`) for Afg.
      ...(isAfg ? {} : { email }),
      ...custom,
    };
  }, [oxygenId, email, analyticsId, region, accountId, projectId, isAfg]);
};

/**
 * Bridges the React context value (written by SimpleLaunchDarklyProvider) into
 * the global Zustand store so that feature-flag hooks work from micro-frontends
 * that run under a different React version or a separate React root — contexts
 * don't propagate across React instances, but the Zustand store is a plain JS
 * singleton on `window` and is accessible from any React tree.
 *
 * The feature-flag hooks read exclusively from the Zustand store. On the first
 * render the store is still undefined (the effect hasn't committed yet), so the
 * hooks return { loading: true } rather than latching a default value — which
 * keeps useAlloyFeatureFlagOnce from settling before the real flag arrives.
 */
const AlloyLaunchDarklyContextSync = () => {
  const ldContext = useContext(AlloyLaunchDarklyContext);
  const { setLdContextValue } = useAlloyLaunchDarklyStore();

  useEffect(() => {
    setLdContextValue(ldContext);
  }, [ldContext, setLdContextValue]);

  return null;
};

const useAlloyLaunchDarklyMetaData = (): AlloyLaunchDarklyContextValue => {
  return useAlloyLaunchDarklyStore();
};

/**
 * Returns the current value of a feature flag from the ACCWBPLT LaunchDarkly project.
 * For teams that need to use their own LaunchDarkly project, use createLaunchDarklyContext instead.
 */
const useAlloyFeatureFlag = <FlagValue,>(
  flagName: string,
  defaultValue?: FlagValue,
) => {
  const { ldContextValue } = useAlloyLaunchDarklyStore();
  return useFeatureFlagCore(ldContextValue, flagName, defaultValue);
};

const useAlloyFeatureFlagOnce = <FlagValue,>(
  flagName: string,
  defaultValue?: FlagValue,
) => {
  const result = useAlloyFeatureFlag(flagName, defaultValue);
  return useFeatureFlagOnceCore(result, defaultValue);
};

const useAlloyAllFeatureFlags = () => {
  const { ldContextValue } = useAlloyLaunchDarklyStore();
  return useAllFeatureFlagsCore(ldContextValue);
};

type AlloyLaunchDarklyProviderProps = Omit<
  LaunchDarklyProviderProps,
  'apiKey'
> & {
  // Make apiKey optional
  apiKey?: LaunchDarklyProviderProps['apiKey'];
};

const AlloyLaunchDarklyProvider: ComponentType<
  AlloyLaunchDarklyProviderProps
> = ({ children, ...restProps }) => {
  const env = getEnv();
  const partition = getPartition();
  const apiKey = useMemo(() => {
    if (!partition) {
      throw new Error(
        'Unable to determine partition for LaunchDarkly client ID',
      );
    }

    const partitionKeys = LD_API_KEYS[partition];
    if (!partitionKeys) {
      throw new Error(
        `Unsupported partition for LaunchDarkly client ID: "${partition}"`,
      );
    }

    const key = partitionKeys[env];
    if (!key) {
      throw new Error(
        `LaunchDarkly partition "${partition}" is not supported in env "${env}"`,
      );
    }

    return key;
  }, [env, partition]);
  const region = getRegion();
  const { user, accountId, projectId, setUser, setProjectId, setAccountId } =
    useAlloyLaunchDarklyStore();
  const ldUser = useAlloyLaunchDarklyUser(user, region, accountId, projectId);
  const options = useMemo(
    () => ({
      allAttributesPrivate: true,
      sendEventsOnlyForVariation: true,
    }),
    [],
  );

  const alloyLaunchDarklyUserContextValue = useMemo(
    () => ({
      projectId,
      accountId,
      user,
      setUser,
      setProjectId,
      setAccountId,
    }),
    [projectId, accountId, user, setUser, setProjectId, setAccountId],
  );

  return (
    <AlloyLaunchDarklyUserContext.Provider
      value={alloyLaunchDarklyUserContextValue}
    >
      <SimpleLaunchDarklyProvider
        user={ldUser}
        apiKey={apiKey}
        options={options}
        {...restProps}
      >
        <AlloyLaunchDarklyContextSync />
        {children}
      </SimpleLaunchDarklyProvider>
    </AlloyLaunchDarklyUserContext.Provider>
  );
};

export {
  useAlloyFeatureFlag,
  useAlloyFeatureFlagOnce,
  useAlloyAllFeatureFlags,
  useAlloyLaunchDarklyMetaData,
  AlloyLaunchDarklyProvider,
};
